import { Box, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';
import { Communication, CommunicationListProps } from '../types/types';
import CommunicationItem from './CommunicationItem';
import CommunicationSkeleton from './Skeletons/CommunicationSkeleton';
import SpriteIcon from '../../../core-components/assets/Icon';
import ErrorScreen from '../../../core-components/theme/components/ErrorScreen';
import { useNavigate } from 'react-router-dom';
import { bottomIndicatorStyles, emptyContainerStyles, endMessageStyles, errorContainerStyles, listContainerStyles, loadingContainerStyles, skeletonItemStyles } from '../styles/styles';


 const CommunicationList: React.FC<CommunicationListProps> = ({
    communicationList,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    rootRef,
    handleCommunicationStatus,
    onItemNavigate
}) => {
    const { t } = useTranslation();
    const sentinelRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    useIntersectionObserver(
         rootRef,
         sentinelRef,
         () => {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
        hasNextPage,
        isLoading
    );

    const handleItemClick = (communication: Communication) => {
      if (communication.multipleConversationExists) {
        navigate(`/communication?communicationId=${communication.communicationId}`);
        if (onItemNavigate) onItemNavigate();
      }
    };

    if (isLoading) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={loadingContainerStyles}>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <Box key={index} sx={skeletonItemStyles}>
                            <CommunicationSkeleton />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={errorContainerStyles}>
                    <ErrorScreen icon={<SpriteIcon id='no_data_error' width="201" height="201"/>} message={`${t('communication.list.failedToLoad', 'Failed to load communications')}: ${error?.message || t('communication.list.unknownError', 'Unknown error')}`} />
                </Box>
            </Box>
        );
    }

    if (!communicationList || communicationList.length === 0) {
        return (
            <Box sx={listContainerStyles}>
                <Box sx={emptyContainerStyles}>
                    <SpriteIcon id='no_notification' fill='none' height='"auto"' width="100%"/>
                    <Typography variant='h6' color="text.secondary">{t('communication.list.empty', 'You have no communications')}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={listContainerStyles}>
            {communicationList.map((communication, index) => (
                <CommunicationItem
                    key={index}
                    communication={communication}
                    handleStatus={handleCommunicationStatus}
                    onClick={() => handleItemClick(communication)}
                />
            ))}
            <div ref={sentinelRef} style={{ height: '1px' }} />
            {isFetchingNextPage && (
                <Box sx={bottomIndicatorStyles}>
                    <CommunicationSkeleton />
                </Box>
            )}
            {!hasNextPage && communicationList.length > 6 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={endMessageStyles}
                >
                    {t('notifications.list.endReached', "You've reached the end")}
                </Typography>
            )}
        </Box>
    );
};  

export default CommunicationList; 